import { useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, ClipboardCheck, FileText } from 'lucide-react';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { cn } from '../../lib/utils';
import type { Account, VATReadinessIssue, VATSummary } from '../../services/types';

export type VatWorkspaceMode = 'summary' | 'review' | 'filing';

const ISSUE_LABELS: Record<string, string> = {
    INPUT_VAT_UNREVIEWED: 'Input VAT review pending',
    SUPPLIER_TRN_MISSING: 'Supplier TRN missing',
    SALES_CUSTOMER_MISSING: 'Sales invoice customer missing',
    SALES_EMIRATE_MISSING: 'Sales/customer emirate missing',
    INVOICE_TAX_SNAPSHOT_MISSING: 'Sales tax snapshot issue',
    SUPPLIER_TAX_SNAPSHOT_MISSING: 'Purchase tax snapshot issue',
    DOCUMENT_TOTAL_MISMATCH: 'Document total mismatch',
    DUPLICATE_SUPPLIER_REFERENCE: 'Duplicate supplier reference',
};

const UAE_EMIRATES = [
    'Abu Dhabi',
    'Dubai',
    'Sharjah',
    'Ajman',
    'Umm Al Quwain',
    'Ras Al Khaimah',
    'Fujairah',
];

function money(value: number | null | undefined) {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(Number(value || 0));
}

function IssueSummary({ issues }: { issues: VATReadinessIssue[] }) {
    const groups = useMemo(() => {
        const grouped = new Map<string, VATReadinessIssue[]>();
        issues.forEach((issue) => {
            grouped.set(issue.code, [...(grouped.get(issue.code) || []), issue]);
        });
        return Array.from(grouped.entries()).sort((left, right) => right[1].length - left[1].length);
    }, [issues]);

    if (groups.length === 0) {
        return (
            <div className="flex items-center gap-3 border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
                <CheckCircle2 className="h-5 w-5" />
                No VAT filing blockers remain for this period.
            </div>
        );
    }

    return (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {groups.map(([code, rows]) => (
                <div key={code} className="border border-amber-200 bg-amber-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                        <div>
                            <div className="text-sm font-semibold text-amber-950">
                                {ISSUE_LABELS[code] || code.replaceAll('_', ' ')}
                            </div>
                            <div className="mt-1 text-xs text-amber-800">
                                {rows[0]?.suggested_action || 'Finance review is required.'}
                            </div>
                        </div>
                        <span className="min-w-8 border border-amber-300 bg-white px-2 py-1 text-center text-sm font-semibold text-amber-950">
                            {rows.length}
                        </span>
                    </div>
                </div>
            ))}
        </div>
    );
}

function VatMetricCards({ vat }: { vat: VATSummary }) {
    const metrics = [
        ['Taxable Sales', vat.taxable_sales],
        ['Output VAT', vat.output_vat],
        ['Taxable Purchases', vat.taxable_purchases],
        ['Confirmed Input VAT', vat.input_vat],
        [vat.net_payable >= 0 ? 'Net VAT Payable' : 'Net VAT Receivable', Math.abs(vat.net_payable)],
    ] as const;

    return (
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
            {metrics.map(([label, value]) => (
                <div key={label} className="border border-slate-200 bg-slate-50 px-4 py-4">
                    <div className="text-xs font-medium uppercase text-slate-500">{label}</div>
                    <div className="mt-2 text-2xl font-semibold text-slate-950">{money(value)}</div>
                </div>
            ))}
        </div>
    );
}

function InlineMasterDataFixes({
    issues,
    busy,
    onSupplierTrnUpdate,
    onCustomerEmirateUpdate,
}: {
    issues: VATReadinessIssue[];
    busy: boolean;
    onSupplierTrnUpdate: (supplierId: number, trn: string) => Promise<void>;
    onCustomerEmirateUpdate: (customerId: number, emirate: string) => Promise<void>;
}) {
    const [supplierTrns, setSupplierTrns] = useState<Record<number, string>>({});
    const [customerEmirates, setCustomerEmirates] = useState<Record<number, string>>({});
    const supplierIssues = Array.from(new Map(
        issues
            .filter((issue) => issue.code === 'SUPPLIER_TRN_MISSING' && issue.party_id)
            .map((issue) => [Number(issue.party_id), issue])
    ).values());
    const customerIssues = Array.from(new Map(
        issues
            .filter((issue) => issue.code === 'SALES_EMIRATE_MISSING' && issue.party_id)
            .map((issue) => [Number(issue.party_id), issue])
    ).values());
    const invoiceCustomerIssues = issues.filter(
        (issue) =>
            issue.code === 'SALES_CUSTOMER_MISSING'
            || (issue.code === 'SALES_EMIRATE_MISSING' && !issue.party_id)
    );

    if (supplierIssues.length === 0 && customerIssues.length === 0 && invoiceCustomerIssues.length === 0) return null;

    return (
        <div className="space-y-3">
            <h3 className="text-base font-semibold text-slate-900">Master Data Corrections</h3>
            <div className="overflow-x-auto border border-slate-200">
                <table className="w-full min-w-[760px] text-sm">
                    <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                        <tr>
                            <th className="px-3 py-2">Issue</th>
                            <th className="px-3 py-2">Party</th>
                            <th className="px-3 py-2">Document</th>
                            <th className="px-3 py-2">Correction</th>
                            <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                        {supplierIssues.map((issue) => (
                            <tr key={`supplier-${issue.party_id}`}>
                                <td className="px-3 py-2">Supplier TRN</td>
                                <td className="px-3 py-2">{issue.party_name || '-'}</td>
                                <td className="px-3 py-2">{issue.document_no || `#${issue.document_id}`}</td>
                                <td className="px-3 py-2">
                                    <Input
                                        aria-label={`TRN for ${issue.party_name || issue.party_id}`}
                                        value={supplierTrns[Number(issue.party_id)] || ''}
                                        onChange={(event) => setSupplierTrns((current) => ({
                                            ...current,
                                            [Number(issue.party_id)]: event.target.value,
                                        }))}
                                        placeholder="15-digit TRN"
                                    />
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <Button
                                        size="sm"
                                        disabled={busy}
                                        onClick={() => void onSupplierTrnUpdate(
                                            Number(issue.party_id),
                                            supplierTrns[Number(issue.party_id)] || ''
                                        )}
                                    >
                                        Save TRN
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {customerIssues.map((issue) => (
                            <tr key={`customer-${issue.party_id}`}>
                                <td className="px-3 py-2">Customer emirate</td>
                                <td className="px-3 py-2">{issue.party_name || '-'}</td>
                                <td className="px-3 py-2">{issue.document_no || `#${issue.document_id}`}</td>
                                <td className="px-3 py-2">
                                    <select
                                        aria-label={`Emirate for ${issue.party_name || issue.party_id}`}
                                        className="h-9 w-full border border-slate-200 bg-white px-3 text-sm"
                                        value={customerEmirates[Number(issue.party_id)] || ''}
                                        onChange={(event) => setCustomerEmirates((current) => ({
                                            ...current,
                                            [Number(issue.party_id)]: event.target.value,
                                        }))}
                                    >
                                        <option value="">Select emirate</option>
                                        {UAE_EMIRATES.map((emirate) => <option key={emirate} value={emirate}>{emirate}</option>)}
                                    </select>
                                </td>
                                <td className="px-3 py-2 text-right">
                                    <Button
                                        size="sm"
                                        disabled={busy}
                                        onClick={() => void onCustomerEmirateUpdate(
                                            Number(issue.party_id),
                                            customerEmirates[Number(issue.party_id)] || ''
                                        )}
                                    >
                                        Save Emirate
                                    </Button>
                                </td>
                            </tr>
                        ))}
                        {invoiceCustomerIssues.map((issue) => (
                            <tr key={`invoice-customer-${issue.document_id || issue.document_no}`}>
                                <td className="px-3 py-2">Sales invoice customer</td>
                                <td className="px-3 py-2">{issue.party_name || 'No customer linked'}</td>
                                <td className="px-3 py-2">{issue.document_no || `#${issue.document_id}`}</td>
                                <td className="px-3 py-2 text-slate-600">
                                    Assign a customer on the sales invoice, then update the customer emirate if required.
                                </td>
                                <td className="px-3 py-2 text-right">
                                    {issue.document_id ? (
                                        <a
                                            className="inline-flex h-9 items-center justify-center border border-slate-200 bg-white px-3 text-sm font-medium text-slate-900 hover:bg-slate-50"
                                            href={`/legacy/invoices/${issue.document_id}`}
                                            aria-label={`Open invoice ${issue.document_no || issue.document_id}`}
                                        >
                                            Open Invoice
                                        </a>
                                    ) : (
                                        <span className="text-xs text-slate-500">Open invoice register</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function VatReviewReport({
    vat,
    accounts,
    selectedBillIds,
    busy,
    onSelectionChange,
    onBulkReview,
    onSupplierTrnUpdate,
    onCustomerEmirateUpdate,
}: {
    vat: VATSummary;
    accounts: Account[];
    selectedBillIds: number[];
    busy: boolean;
    onSelectionChange: (billIds: number[]) => void;
    onBulkReview: (recoveryPercent: number, accountId?: number | null) => Promise<void>;
    onSupplierTrnUpdate: (supplierId: number, trn: string) => Promise<void>;
    onCustomerEmirateUpdate: (customerId: number, emirate: string) => Promise<void>;
}) {
    const [partialPercent, setPartialPercent] = useState('50');
    const [nonrecoverableAccountId, setNonrecoverableAccountId] = useState('');
    const unreviewed = vat.issues.filter(
        (issue) => issue.code === 'INPUT_VAT_UNREVIEWED' && issue.document_id
    );
    const eligibleAccounts = accounts.filter(
        (account) =>
            account.is_active !== false
            && account.is_postable !== false
            && account.has_children !== true
            && ['asset', 'expense'].includes(String(account.type || '').toLowerCase())
    );

    const toggleBill = (billId: number) => {
        onSelectionChange(
            selectedBillIds.includes(billId)
                ? selectedBillIds.filter((id) => id !== billId)
                : [...selectedBillIds, billId]
        );
    };

    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-xl font-semibold text-slate-950">VAT Review Report</h3>
                <p className="mt-1 text-sm text-slate-600">
                    Confirm recoverable purchase VAT and clear the master-data blockers for the selected period.
                </p>
            </div>
            <IssueSummary issues={vat.issues} />

            {unreviewed.length > 0 && (
                <div className="space-y-3">
                    <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                        <div>
                            <h4 className="text-base font-semibold text-slate-900">Input VAT Recovery Review</h4>
                            <div className="mt-1 text-xs text-slate-500">{selectedBillIds.length} bill(s) selected</div>
                        </div>
                        <div className="flex flex-wrap items-end gap-2">
                            <Button
                                size="sm"
                                disabled={busy || selectedBillIds.length === 0}
                                onClick={() => void onBulkReview(100, null)}
                            >
                                Confirm 100% Recoverable
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy || selectedBillIds.length === 0 || !nonrecoverableAccountId}
                                onClick={() => void onBulkReview(0, Number(nonrecoverableAccountId))}
                            >
                                Mark Non-Recoverable
                            </Button>
                            <label className="space-y-1 text-xs font-medium text-slate-600">
                                Partial %
                                <Input
                                    className="w-24"
                                    type="number"
                                    min="0.01"
                                    max="99.99"
                                    value={partialPercent}
                                    onChange={(event) => setPartialPercent(event.target.value)}
                                />
                            </label>
                            <label className="space-y-1 text-xs font-medium text-slate-600">
                                Non-recoverable account
                                <select
                                    className="h-9 min-w-56 border border-slate-200 bg-white px-3 text-sm"
                                    value={nonrecoverableAccountId}
                                    onChange={(event) => setNonrecoverableAccountId(event.target.value)}
                                >
                                    <option value="">Select account</option>
                                    {eligibleAccounts.map((account) => (
                                        <option key={account.id} value={account.id}>{account.code} - {account.name}</option>
                                    ))}
                                </select>
                            </label>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={busy || selectedBillIds.length === 0 || !nonrecoverableAccountId}
                                onClick={() => void onBulkReview(Number(partialPercent), Number(nonrecoverableAccountId))}
                            >
                                Confirm Partial Recovery
                            </Button>
                        </div>
                    </div>

                    <div className="overflow-x-auto border border-slate-200">
                        <table className="w-full min-w-[900px] text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="w-12 px-3 py-2">
                                        <span className="sr-only">Select</span>
                                    </th>
                                    <th className="px-3 py-2">Bill</th>
                                    <th className="px-3 py-2">Date</th>
                                    <th className="px-3 py-2">Supplier</th>
                                    <th className="px-3 py-2">TRN</th>
                                    <th className="px-3 py-2 text-right">Taxable</th>
                                    <th className="px-3 py-2 text-right">VAT</th>
                                    <th className="px-3 py-2">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {unreviewed.map((issue) => {
                                    const billId = Number(issue.document_id);
                                    return (
                                        <tr key={billId}>
                                            <td className="px-3 py-2">
                                                <input
                                                    type="checkbox"
                                                    aria-label={`Select ${issue.document_no || `bill ${billId}`}`}
                                                    checked={selectedBillIds.includes(billId)}
                                                    onChange={() => toggleBill(billId)}
                                                />
                                            </td>
                                            <td className="px-3 py-2 font-medium">{issue.document_no || `#${billId}`}</td>
                                            <td className="px-3 py-2">{issue.document_date || '-'}</td>
                                            <td className="px-3 py-2">{issue.party_name || '-'}</td>
                                            <td className="px-3 py-2">{issue.party_trn || '-'}</td>
                                            <td className="px-3 py-2 text-right">{money(issue.taxable_amount)}</td>
                                            <td className="px-3 py-2 text-right">{money(issue.vat_amount)}</td>
                                            <td className="px-3 py-2">{issue.recovery_status || 'UNREVIEWED'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <InlineMasterDataFixes
                issues={vat.issues}
                busy={busy}
                onSupplierTrnUpdate={onSupplierTrnUpdate}
                onCustomerEmirateUpdate={onCustomerEmirateUpdate}
            />
        </div>
    );
}

function VatFilingReport({ vat }: { vat: VATSummary }) {
    const ready = vat.readiness.status === 'ready';
    return (
        <div className="space-y-5">
            <div>
                <h3 className="text-xl font-semibold text-slate-950">
                    {ready ? 'Filing Ready' : 'Provisional Filing Report'}
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                    {ready
                        ? 'The selected period has no blocking VAT readiness issues.'
                        : 'Unconfirmed Input VAT is excluded from the claimable Input VAT total until finance review is complete.'}
                </p>
            </div>
            <VatMetricCards vat={vat} />

            <div className="grid gap-5 xl:grid-cols-2">
                <div className="space-y-3">
                    <h4 className="text-base font-semibold text-slate-900">Output VAT by Emirate</h4>
                    <div className="overflow-x-auto border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="px-3 py-2">Emirate</th>
                                    <th className="px-3 py-2 text-right">Taxable</th>
                                    <th className="px-3 py-2 text-right">VAT</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {vat.emirate_summary.map((row) => (
                                    <tr key={row.emirate}>
                                        <td className="px-3 py-2">{row.emirate}</td>
                                        <td className="px-3 py-2 text-right">{money(row.taxable_amount)}</td>
                                        <td className="px-3 py-2 text-right">{money(row.vat_amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="space-y-3">
                    <h4 className="text-base font-semibold text-slate-900">Monthly VAT Summary</h4>
                    <div className="overflow-x-auto border border-slate-200">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
                                <tr>
                                    <th className="px-3 py-2">Month</th>
                                    <th className="px-3 py-2 text-right">Output VAT</th>
                                    <th className="px-3 py-2 text-right">Input VAT</th>
                                    <th className="px-3 py-2 text-right">Net VAT</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200">
                                {vat.monthly_summary.map((row) => (
                                    <tr key={row.month}>
                                        <td className="px-3 py-2">{row.month}</td>
                                        <td className="px-3 py-2 text-right">{money(row.output_vat)}</td>
                                        <td className="px-3 py-2 text-right">{money(row.input_vat)}</td>
                                        <td className="px-3 py-2 text-right">{money(row.net_vat)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            {!ready && <IssueSummary issues={vat.issues} />}
        </div>
    );
}

export function VatFilingWorkbench({
    vat,
    mode,
    accounts,
    selectedBillIds,
    busy,
    onModeChange,
    onSelectionChange,
    onBulkReview,
    onSupplierTrnUpdate,
    onCustomerEmirateUpdate,
}: {
    vat: VATSummary;
    mode: VatWorkspaceMode;
    accounts: Account[];
    selectedBillIds: number[];
    busy: boolean;
    onModeChange: (mode: VatWorkspaceMode) => void;
    onSelectionChange: (billIds: number[]) => void;
    onBulkReview: (recoveryPercent: number, accountId?: number | null) => Promise<void>;
    onSupplierTrnUpdate: (supplierId: number, trn: string) => Promise<void>;
    onCustomerEmirateUpdate: (customerId: number, emirate: string) => Promise<void>;
}) {
    return (
        <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 pb-4">
                <Button
                    size="sm"
                    variant={mode === 'summary' ? 'default' : 'outline'}
                    onClick={() => onModeChange('summary')}
                >
                    <FileText className="mr-2 h-4 w-4" />
                    VAT Filing Summary
                </Button>
                <Button
                    size="sm"
                    variant={mode === 'review' ? 'default' : 'outline'}
                    onClick={() => onModeChange('review')}
                >
                    <ClipboardCheck className="mr-2 h-4 w-4" />
                    View Review Report
                </Button>
                <Button
                    size="sm"
                    variant={mode === 'filing' ? 'default' : 'outline'}
                    onClick={() => onModeChange('filing')}
                >
                    <AlertTriangle className={cn('mr-2 h-4 w-4', vat.readiness.status === 'ready' && 'text-emerald-200')} />
                    View Filing Report
                </Button>
            </div>

            {mode === 'summary' && (
                <div className="space-y-5">
                    <div className={cn(
                        'border px-4 py-4',
                        vat.readiness.status === 'ready'
                            ? 'border-emerald-200 bg-emerald-50'
                            : 'border-amber-200 bg-amber-50'
                    )}>
                        <div className="font-semibold text-slate-950">
                            {vat.readiness.status === 'ready' ? 'Filing pack ready' : 'Finance review required'}
                        </div>
                        <div className="mt-1 text-sm text-slate-700">
                            {vat.readiness.blocking_issue_count} blocking issue(s) across {vat.total} tax document(s).
                        </div>
                    </div>
                    <VatMetricCards vat={vat} />
                    <IssueSummary issues={vat.issues} />
                </div>
            )}

            {mode === 'review' && (
                <VatReviewReport
                    vat={vat}
                    accounts={accounts}
                    selectedBillIds={selectedBillIds}
                    busy={busy}
                    onSelectionChange={onSelectionChange}
                    onBulkReview={onBulkReview}
                    onSupplierTrnUpdate={onSupplierTrnUpdate}
                    onCustomerEmirateUpdate={onCustomerEmirateUpdate}
                />
            )}

            {mode === 'filing' && <VatFilingReport vat={vat} />}
        </div>
    );
}
